import { useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  FileText,
  CreditCard,
  RefreshCw,
  MessageSquare,
  Star
} from "lucide-react";
import { useState, useEffect } from "react";
import { applicationsAPI, paymentsAPI, renewalsAPI, contactAPI, reviewsAPI } from "../services/api";

export function SearchResults() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState({
    applications: [],
    payments: [],
    renewals: [],
    contacts: [],
    reviews: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data from APIs
      const [applicationsRes, paymentsRes, renewalsRes, contactsRes, reviewsRes] = await Promise.all([
        applicationsAPI.getAll({ limit: 100 }),
        paymentsAPI.getAll({ limit: 100 }),
        renewalsAPI.getAll({ limit: 100 }),
        contactAPI.getAll({ limit: 100 }),
        reviewsAPI.getAll({ limit: 100 })
      ]);

      // Store raw data
      const applications = applicationsRes.data || [];
      const payments = paymentsRes.data || [];
      const renewals = renewalsRes.data || [];
      const contacts = contactsRes.data || [];
      const reviews = reviewsRes.data || [];

      // Filter based on search query
      const searchTerm = query.toLowerCase();

      const filteredResults = {
        applications: applications.filter(a =>
          (a.full_name || a.name || '').toLowerCase().includes(searchTerm) ||
          (a.application_number || a.applicationId || '').toLowerCase().includes(searchTerm) ||
          (a.phone || '').toLowerCase().includes(searchTerm) ||
          (a.email || '').toLowerCase().includes(searchTerm)
        ),
        payments: payments.filter(p =>
          (p.name || '').toLowerCase().includes(searchTerm) ||
          (p.payment_number || p.transactionId || '').toLowerCase().includes(searchTerm) ||
          (p.phone || '').toLowerCase().includes(searchTerm)
        ),
        renewals: renewals.filter(r =>
          (r.member_name || r.full_name || '').toLowerCase().includes(searchTerm) ||
          (r.renewal_number || '').toLowerCase().includes(searchTerm) ||
          (r.member_phone || r.phone || '').toLowerCase().includes(searchTerm)
        ),
        contacts: contacts.filter(c =>
          (c.name || '').toLowerCase().includes(searchTerm) ||
          (c.email || '').toLowerCase().includes(searchTerm) ||
          (c.phone || '').toLowerCase().includes(searchTerm) ||
          (c.subject || '').toLowerCase().includes(searchTerm)
        ),
        reviews: reviews.filter(r =>
          (r.name || '').toLowerCase().includes(searchTerm) ||
          (r.role || '').toLowerCase().includes(searchTerm) ||
          (r.text || r.review_text || '').toLowerCase().includes(searchTerm)
        )
      };

      setResults(filteredResults);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to fetch search results');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (query) {
      fetchAllData();
    } else {
      setResults({
        applications: [],
        payments: [],
        renewals: [],
        contacts: [],
        reviews: []
      });
      setLoading(false);
    }
  }, [query]);

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
      case 'approved':
      case 'completed':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Active</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">Pending</span>;
      case 'expired':
      case 'rejected':
      case 'failed':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Inactive</span>;
      default:
        return null;
    }
  };

  const totalResults = Object.values(results).reduce((sum, arr) => sum + arr.length, 0);

  const ResultSection = ({ title, icon: Icon, items, children }) => {
    if (items.length === 0) return null;
    
    return (
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-[#111111] mb-4 flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#C46A1B]" />
          {title} ({items.length})
        </h2>
        <div className="space-y-3">
          {children}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-[#111111] mb-2">Search Results</h1>
        <p className="text-gray-600">
          {loading ? 'Searching...' : `Found ${totalResults} results for "${query}"`}
        </p>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#C46A1B] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : totalResults === 0 ? (
          <div className="mt-8 text-center text-gray-500 py-12">
            <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-lg">No results found</p>
            <p className="text-sm mt-1">Try searching with different keywords</p>
          </div>
        ) : (
          <div className="mt-8">
            {/* Applications */}
            <ResultSection title="Applications" icon={FileText} items={results.applications}>
              {results.applications.map(app => (
                <Link
                  key={app.id}
                  to={`/applications/${app.id}`}
                  className="block p-4 border rounded-lg hover:shadow-md transition hover:border-[#C46A1B]"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-[#111111]">{app.full_name || app.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{app.application_number || app.applicationId} • {app.card_type || app.type}</p>
                      <p className="text-xs text-gray-400 mt-1">Applied: {app.created_at || app.date}</p>
                    </div>
                    {getStatusBadge(app.status)}
                  </div>
                </Link>
              ))}
            </ResultSection>

            {/* Payments */}
            <ResultSection title="Payments" icon={CreditCard} items={results.payments}>
              {results.payments.map(payment => (
                <Link
                  key={payment.id}
                  to={payment.path}
                  className="block p-4 border rounded-lg hover:shadow-md transition hover:border-[#C46A1B]"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-[#111111]">{payment.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{payment.payment_number || payment.transactionId} • {payment.payment_method || payment.method}</p>
                      <div className="flex gap-4 mt-2">
                        <span className="text-sm font-medium text-[#C46A1B]">₹{payment.amount}</span>
                        <span className="text-xs text-gray-400">{payment.payment_date || payment.date}</span>
                      </div>
                    </div>
                    {getStatusBadge(payment.status)}
                  </div>
                </Link>
              ))}
            </ResultSection>

            {/* Renewals */}
            <ResultSection title="Renewals" icon={RefreshCw} items={results.renewals}>
              {results.renewals.map(renewal => (
                <Link
                  key={renewal.id}
                  to={`/renew/${renewal.id}`}
                  className="block p-4 border rounded-lg hover:shadow-md transition hover:border-[#C46A1B]"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-[#111111]">{renewal.member_name || renewal.full_name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{renewal.renewal_number}</p>
                      <p className="text-xs text-gray-400 mt-1">Renewed: {renewal.renewal_date || renewal.created_at}</p>
                    </div>
                    {getStatusBadge(renewal.status)}
                  </div>
                </Link>
              ))}
            </ResultSection>

            {/* Contact Messages */}
            <ResultSection title="Contact Messages" icon={MessageSquare} items={results.contacts}>
              {results.contacts.map(contact => (
                <Link
                  key={contact.id}
                  to="/contact-messages"
                  className="block p-4 border rounded-lg hover:shadow-md transition hover:border-[#C46A1B]"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-[#111111]">{contact.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{contact.subject}</p>
                      <p className="text-xs text-gray-400 mt-1">{contact.email} • {contact.phone}</p>
                    </div>
                    {getStatusBadge(contact.status)}
                  </div>
                </Link>
              ))}
            </ResultSection>

            {/* Reviews */}
            <ResultSection title="Reviews" icon={Star} items={results.reviews}>
              {results.reviews.map(review => (
                <Link
                  key={review.id}
                  to="/reviews"
                  className="block p-4 border rounded-lg hover:shadow-md transition hover:border-[#C46A1B]"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-[#111111]">{review.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{review.role}</p>
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2">{review.text || review.review_text}</p>
                      <div className="flex gap-1 mt-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-3 h-3 ${i < (review.rating || 5) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </ResultSection>
          </div>
        )}
      </div>
    </div>
  );
}
